import {
    DefaultApi,
    CustomerInput,
    FileUpload,
} from '@hello-pimster/pimster-crm-sdk';
import { useAnalytics } from '@lib/analytics/analyticsContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Axios from 'axios';
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';

import { createApiCrmClient, ResponseInterceptor } from '../client';
import {
    CHAT_CONTROLLER_COMPLETION_PATH,
    CUSTOMER_INPUT_CONTROLLER_ATTACHMENT_PATH,
} from '../constants';
import { FetchClient } from '../types/FetchClient';
import { FileUploadClient } from '../types/FileUploadClient';
import { eventSourceFactory } from '../utils/eventSourceFactory';
import { fileUploadFactory } from '../utils/fileUploadFactory';

interface IApiCrmContext {
    client: DefaultApi;
    fetchClient: FetchClient;
    fileUploadClient: FileUploadClient;
    addInterceptor: (newInterceptor: ResponseInterceptor) => void;
}

export const ApiCrmContext = createContext<IApiCrmContext>(
    {} as IApiCrmContext
);

interface Props {
    children: ReactNode;
}

const ApiCrmProvider = ({ children }: Props) => {
    const { analytics } = useAnalytics();
    const [interceptors, setInterceptors] = useState<ResponseInterceptor[]>([]);

    /**
     * Axios API
     */
    const userIdInterceptor: ResponseInterceptor = useMemo(
        () => ({
            fulfilled: (res: Axios.AxiosResponse<CustomerInput>) => {
                const { UUID } = res.data.customerProfile!.customerIdentity!;
                analytics.identify(UUID);
                return res;
            },
            condition: (res: Axios.AxiosRequestConfig<CustomerInput>) => {
                /// Only run when receiving a customer identity
                return !!res.data?.customerProfile?.customerIdentity?.UUID;
            },
        }),
        [analytics]
    );

    /**
     * Swagger Generated API
     */
    const client = useMemo(() => {
        return createApiCrmClient({}, [userIdInterceptor, ...interceptors]);
    }, [userIdInterceptor, interceptors]);

    /**
     * Fetch API
     */
    const fetchClient = useMemo(
        () => ({
            agentControllerCompletion: (
                prompt: string,
                agentId: number,
                chatId: string,
                onResponse?: (response: Response) => void
            ) => {
                const fn = eventSourceFactory(
                    CHAT_CONTROLLER_COMPLETION_PATH(chatId)
                );

                return fn(
                    { searchParams: new URLSearchParams({ prompt }) },
                    onResponse
                );
            },
        }),
        []
    );

    /**
     * File Upload API
     */
    const fileUploadClient: FileUploadClient = useMemo(() => {
        const customerInputControllerAttachmentFn = fileUploadFactory(
            CUSTOMER_INPUT_CONTROLLER_ATTACHMENT_PATH
        );

        return {
            customerInputControllerAttachment: (
                data: FileUpload,
                options?: Axios.AxiosRequestConfig<FormData>
            ): Promise<Axios.AxiosResponse> =>
                customerInputControllerAttachmentFn(data, {
                    ...options,
                    headers: options?.headers,
                }),
        };
    }, []);

    /**
     * Query Client Provider
     */
    const queryClient = useMemo(() => {
        return new QueryClient();
    }, []);

    const addInterceptor = useCallback(
        (newInterceptor: ResponseInterceptor) => {
            setInterceptors((interceptors) => [
                ...interceptors,
                newInterceptor,
            ]);
        },
        []
    );

    return (
        <QueryClientProvider client={queryClient}>
            <ApiCrmContext.Provider
                value={{
                    client,
                    fetchClient,
                    fileUploadClient,
                    addInterceptor,
                }}
            >
                {children}
            </ApiCrmContext.Provider>
        </QueryClientProvider>
    );
};

export default ApiCrmProvider;

export const useApiCrm = () => useContext(ApiCrmContext);
